import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { VENDOR_LABEL, externalIdLabel } from '../api/types'
import {
  type Searchable, filterStations, moveActive, pickOnEnter,
} from '../lib/stationSearch'

interface Props {
  stations: Searchable[]
  /** The station on screen, by code. */
  value: string | null
  onSelect: (code: string) => void
  label?: string
  /** Label beside the control rather than above it (the Analytics header). */
  inline?: boolean
}

/** The station control, searchable: a native select stops being usable once
 *  there are thirty stations and a reader remembers a fragment of one. It is
 *  the ARIA 1.2 combobox pattern with a list popup: type to narrow (the same
 *  matching as the Stations overview), arrows to move, Enter to open, Escape
 *  to back out leaving the station on screen as it was. Focus stays in the
 *  input throughout; the list is driven by aria-activedescendant. */
export function StationPicker({ stations, value, onSelect, label = 'Station',
                                inline = false }: Props) {
  const id = useId()
  const listId = `${id}-list`
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(-1)

  const place = usePlacement(input, open)
  const current = stations.find((s) => s.code === value) ?? null
  const matches = filterStations(stations, query)

  // Keep the highlighted row in view as the arrows move past the list's edge.
  useEffect(() => {
    if (!open || active < 0) return
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const openList = () => {
    if (open) return
    setQuery('')
    // Open on the station already on screen, so the list starts where the
    // reader is rather than at the top.
    setActive(Math.max(0, stations.findIndex((s) => s.code === value)))
    setOpen(true)
  }
  const close = () => { setOpen(false); setQuery(''); setActive(-1) }
  const choose = (code: string | null) => {
    close()
    if (code && code !== value) onSelect(code)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        if (!open) { openList(); return }
        setActive((a) => moveActive(a, event.key === 'ArrowDown' ? 1 : -1, matches.length))
        return
      }
      case 'Enter':
        if (!open) return
        event.preventDefault()
        choose(pickOnEnter(matches, active))
        return
      case 'Escape':
        if (!open) return
        event.preventDefault()
        close()
        return
      case 'Tab':
        if (open) close()
        return
    }
  }

  const activeId = open && matches[active] ? `${id}-opt-${matches[active]!.code}` : undefined
  const shown = open ? query : current?.name ?? ''

  return (
    <div className={inline ? 'field inline picker-field' : 'field picker-field'}>
      <label htmlFor={`${id}-input`}>{label}</label>
      <div className={open ? 'picker open' : 'picker'}>
        <input
          ref={input}
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          placeholder={open ? (current?.name ?? 'Search stations') : 'Choose a station'}
          value={shown}
          onFocus={openList}
          onClick={openList}
          onBlur={close}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            setQuery(event.target.value)
            setActive(0)
            if (!open) setOpen(true)
          }}
        />
        <span className="picker-chevron" aria-hidden="true" />
        {open && place && createPortal(
          <ul ref={list} id={listId} role="listbox" aria-label={`${label} list`}
              className={place.above ? 'picker-list above' : 'picker-list'}
              style={place.style}
              // Keeps focus in the input when the list's own scrollbar is
              // dragged, which would otherwise blur it and close the list.
              onMouseDown={(event) => event.preventDefault()}>
            {matches.length ? matches.map((s, i) => (
              <li key={s.code} id={`${id}-opt-${s.code}`} role="option"
                  data-index={i}
                  aria-selected={i === active}
                  className={[i === active ? 'active' : '',
                              s.code === value ? 'current' : ''].filter(Boolean).join(' ')
                    || undefined}
                  // mousedown, not click: the input's blur would close the
                  // list before a click on it could land.
                  onMouseDown={(event) => { event.preventDefault(); choose(s.code) }}
                  onMouseMove={() => { if (i !== active) setActive(i) }}>
                <span className="picker-name">{s.name}</span>
                <span className="picker-meta">
                  {VENDOR_LABEL[s.vendor]} · {externalIdLabel(s.vendor)} {s.external_id}
                </span>
              </li>
            )) : (
              <li className="picker-empty" role="presentation">
                No station matches “{query.trim()}”.
              </li>
            )}
          </ul>,
          document.body,
        )}
      </div>
    </div>
  )
}

const GAP = 4
const EDGE = 8
const PREFERRED_HEIGHT = 320

/** Where the list goes. It is drawn on the page itself, fixed to the input,
 *  because the Explore sidebar scrolls and so clips anything positioned
 *  inside it. Below the input, or above it when there is not room below;
 *  at least as wide as the input and wide enough for a name and its id,
 *  never past the window's edge. Followed on scroll and resize. */
function usePlacement(anchor: React.RefObject<HTMLElement>, open: boolean) {
  const [place, setPlace] = useState<{ above: boolean; style: React.CSSProperties } | null>(null)
  useLayoutEffect(() => {
    if (!open) { setPlace(null); return }
    const update = () => {
      const el = anchor.current
      const r = el?.getBoundingClientRect()
      if (!el || !r) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      // The list is placed in the page's units, and the page may be zoomed
      // (styles.css): measurements come back in screen pixels, so they are
      // divided by the zoom before they are used as positions.
      const z = (document.body as HTMLElement & { currentCSSZoom?: number })
        .currentCSSZoom || 1
      const below = vh - r.bottom - GAP - EDGE
      const above = r.top - GAP - EDGE
      const up = below < Math.min(PREFERRED_HEIGHT, 200) && above > below
      const width = Math.min(Math.max(r.width, 320), vw - 2 * EDGE)
      const left = Math.min(Math.max(r.left, EDGE), vw - EDGE - width)
      setPlace({ above: up, style: {
        position: 'fixed', left: left / z, width: width / z,
        maxHeight: Math.max(Math.min(PREFERRED_HEIGHT, up ? above : below), 120) / z,
        ...(up ? { bottom: (vh - r.top + GAP) / z } : { top: (r.bottom + GAP) / z }),
      } })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor, open])
  return place
}
